import React, { useState } from "react";
import type { CarsCompletedProgressSpan } from "../../../types";
import { getCarEffortColor } from "../../../utils/appointmentUtils";
import { Card } from "../../shared";
import { Icons } from "../../shared/Icons";

interface CarsCompletedProgressSummaryProps {
  progressSpan: CarsCompletedProgressSpan | null;
  totalCompletedWithCar: number;
  rangeLabel: string;
  activeProfessionalCount?: number;
}

type MetricsView = "overall" | "service";

function formatAvgMinutes(min: number): string {
  if (!Number.isFinite(min) || min <= 0) return "0 min";

  const totalMinutes = Math.round(min);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}min`;
  if (hours > 0) return `${hours}h`;
  return `${minutes} min`;
}

function formatEffortLabel(effort: string): string {
  const map: Record<string, string> = {
    leve: "Leve",
    medio: "Médio",
    pesado: "Pesado",
  };
  return map[effort.toLowerCase()] ?? effort;
}

function buildProfessionalEstimates(
  carsPerProfessionalPerDay: number,
  activeProfessionalCount?: number,
): Array<{ count: number; label: string; estimate: number }> {
  const base = carsPerProfessionalPerDay ?? 0;
  const estimates: Array<{ count: number; label: string; estimate: number }> = [
    { count: 1, label: "1 profissional", estimate: base },
    { count: 2, label: "2 profissionais", estimate: base * 2 },
  ];

  if (activeProfessionalCount != null && activeProfessionalCount > 2) {
    estimates.push({
      count: activeProfessionalCount,
      label: `${activeProfessionalCount} profissionais`,
      estimate: base * activeProfessionalCount,
    });
  }

  return estimates;
}

function formatVehicleEstimate(count: number): string {
  return `${count} ${count === 1 ? "veículo" : "veículos"}`;
}

export const CarsCompletedProgressSummary: React.FC<
  CarsCompletedProgressSummaryProps
> = ({ progressSpan, totalCompletedWithCar, rangeLabel, activeProfessionalCount }) => {
  const [metricsView, setMetricsView] = useState<MetricsView>("overall");

  if (!progressSpan) return null;

  const overallEstimate = progressSpan.overallEstimatedCarsPer8HourDay ?? 0;
  const professionalEstimates = buildProfessionalEstimates(
    overallEstimate,
    activeProfessionalCount,
  );
  const services = progressSpan.byService ?? [];
  const showServiceToggle = services.length > 0;

  return (
    <Card className="p-4 sm:p-6 border border-gray-100 shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Capacidade e tempo médio
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">{rangeLabel}</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
          {showServiceToggle && (
            <div className="flex bg-gray-100 dark:bg-slate-700 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setMetricsView("overall")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  metricsView === "overall"
                    ? "bg-white dark:bg-slate-800 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                Geral
              </button>
              <button
                type="button"
                onClick={() => setMetricsView("service")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  metricsView === "service"
                    ? "bg-white dark:bg-slate-800 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                Por serviço
              </button>
            </div>
          )}
          <div className="flex items-center gap-2 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 px-4 py-3">
            <Icons.Car sx={{ fontSize: 28 }} className="text-slate-600 dark:text-slate-300" />
            <div>
              <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                Total concluídos com veículo
              </p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                {totalCompletedWithCar}
              </p>
            </div>
          </div>
        </div>
      </div>

      {metricsView === "overall" && (
        <div className="rounded-xl border border-sky-100 dark:border-slate-600 bg-gradient-to-br from-sky-50 to-white dark:from-slate-900 dark:to-slate-800 p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Geral</p>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                {totalCompletedWithCar}{" "}
                {totalCompletedWithCar === 1 ? "veículo" : "veículos"}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-400">
                Tempo médio
              </p>
              <p className="text-lg font-bold text-sky-950 dark:text-sky-100 tabular-nums">
                {formatAvgMinutes(progressSpan.overallAverageMinutes)}
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-emerald-200/80 dark:border-emerald-800 bg-white/70 dark:bg-emerald-950/30 px-3 py-2 mb-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400 mb-2">
              Estimativa / 8h
            </p>
            <div
              className={`grid gap-2 ${
                professionalEstimates.length === 3
                  ? "grid-cols-1 sm:grid-cols-3"
                  : "grid-cols-2"
              }`}
            >
              {professionalEstimates.map(({ count, label, estimate }) => (
                <div
                  key={count}
                  className="rounded-lg border border-emerald-200/80 dark:border-emerald-800 bg-white dark:bg-slate-800 px-2 py-1.5"
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                    {label}
                  </p>
                  <p className="text-sm font-bold text-emerald-950 dark:text-emerald-100 tabular-nums leading-tight">
                    {formatVehicleEstimate(estimate)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {(progressSpan.byEffort ?? []).length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {progressSpan.byEffort.map((row) => (
                <div
                  key={row.effort}
                  className={`rounded-lg border px-2 py-2 ${getCarEffortColor(
                    formatEffortLabel(row.effort),
                  )}`}
                >
                  <p className="text-[10px] font-bold uppercase tracking-wide opacity-80">
                    {formatEffortLabel(row.effort)}
                  </p>
                  <p className="mt-1 text-sm font-semibold tabular-nums">
                    {formatAvgMinutes(row.averageMinutes)}
                  </p>
                  <p className="text-[10px] tabular-nums opacity-80">
                    {row.sampleCount} {row.sampleCount === 1 ? "veículo" : "veículos"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {metricsView === "service" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {services.map((service) => {
            const serviceEstimates = buildProfessionalEstimates(
              service.overallEstimatedCarsPer8HourDay ?? 0,
              activeProfessionalCount,
            );

            return (
            <div
              key={service.serviceId}
              className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/30 p-4"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <p
                    className="text-sm font-semibold text-indigo-950 dark:text-indigo-100 truncate"
                    title={service.serviceName}
                  >
                    {service.serviceName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                    {service.sampleCount}{" "}
                    {service.sampleCount === 1 ? "veículo" : "veículos"}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-400">
                    Tempo médio
                  </p>
                  <p className="text-lg font-bold text-sky-950 dark:text-sky-100 tabular-nums">
                    {formatAvgMinutes(service.overallAverageMinutes)}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-emerald-200/80 dark:border-emerald-800 bg-white/80 dark:bg-emerald-950/30 px-3 py-2 mb-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400 mb-2">
                  Estimativa / 8h
                </p>
                <div
                  className={`grid gap-2 ${
                    serviceEstimates.length === 3
                      ? "grid-cols-1 sm:grid-cols-3"
                      : "grid-cols-2"
                  }`}
                >
                  {serviceEstimates.map(({ count, label, estimate }) => (
                    <div
                      key={count}
                      className="rounded-lg border border-emerald-200/80 dark:border-emerald-800 bg-white dark:bg-slate-800 px-2 py-1.5"
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                        {label}
                      </p>
                      <p className="text-sm font-bold text-emerald-950 dark:text-emerald-100 tabular-nums leading-tight">
                        {formatVehicleEstimate(estimate)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {(service.byEffort ?? []).map((row) => (
                  <div
                    key={row.effort}
                    className={`rounded-lg border px-2 py-2 ${getCarEffortColor(
                      formatEffortLabel(row.effort),
                    )}`}
                  >
                    <p className="text-[10px] font-bold uppercase tracking-wide opacity-80">
                      {formatEffortLabel(row.effort)}
                    </p>
                    <p className="mt-1 text-sm font-semibold tabular-nums">
                      {formatAvgMinutes(row.averageMinutes)}
                    </p>
                    <p className="text-[10px] tabular-nums opacity-80">
                      {row.sampleCount} {row.sampleCount === 1 ? "veículo" : "veículos"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};
